import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
const ROLES = ['Admin', 'Project Manager', 'Developer'];

const ROLE_BADGE = {
  'Admin':           { bg: '#fef2f2', color: '#dc2626', border: '#fca5a5' },
  'Project Manager': { bg: '#eff6ff', color: '#2563eb', border: '#bfdbfe' },
  'Developer':       { bg: '#f0fdf4', color: '#16a34a', border: '#bbf7d0' },
};

const emptyForm = () => ({ name: '', email: '', role: 'Developer', temporary_password: '', is_active: true });

const Users = () => {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');

  // Reset password modal
  const [resetUserId, setResetUserId] = useState(null);
  const [resetUserName, setResetUserName] = useState('');
  const [tempPassword, setTempPassword] = useState('');
  const [resetError, setResetError] = useState('');
  const [resetSaving, setResetSaving] = useState(false);

  // Change own password modal
  const [showOwnReset, setShowOwnReset] = useState(false);
  const [ownCurrent, setOwnCurrent] = useState('');
  const [ownNew, setOwnNew] = useState('');
  const [ownConfirm, setOwnConfirm] = useState('');
  const [ownError, setOwnError] = useState('');
  const [ownSaving, setOwnSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get('/users');
      setUsers(res.data);
    } catch { }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const flash = (msg) => { setSuccessMsg(msg); setTimeout(() => setSuccessMsg(''), 3000); };

  const openCreate = () => { setEditingId(null); setForm(emptyForm()); setFormError(''); setShowModal(true); };
  const openEdit = (u) => {
    setEditingId(u.user_id);
    setForm({ name: u.name, email: u.email, role: u.role, temporary_password: '', is_active: !!u.is_active });
    setFormError('');
    setShowModal(true);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm(p => ({ ...p, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');
    if (!form.name.trim())  { setFormError('Name is required.'); return; }
    if (!form.email.trim()) { setFormError('Email is required.'); return; }
    if (!editingId && !form.temporary_password) { setFormError('Temporary password is required for new users.'); return; }
    if (!editingId && form.temporary_password.length < 6) { setFormError('Password must be at least 6 characters.'); return; }

    setSaving(true);
    try {
      if (editingId) {
        await api.put(`/users/${editingId}`, form);
        flash('User updated successfully.');
      } else {
        await api.post('/users', form);
        flash(`User created. They must change their password on first login.`);
      }
      setShowModal(false);
      load();
    } catch (err) {
      setFormError(err.response?.data?.error || 'Error saving user.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id, name) => {
    if (id === currentUser.user_id) { alert('You cannot delete your own account.'); return; }
    if (!window.confirm(`Delete user "${name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/users/${id}`);
      flash('User deleted.');
      load();
    } catch (err) {
      alert(err.response?.data?.error || 'Error deleting user.');
    }
  };

  const openReset = (u) => {
    setResetUserId(u.user_id);
    setResetUserName(u.name);
    setTempPassword('');
    setResetError('');
  };

  const handleReset = async () => {
    setResetError('');
    if (tempPassword.length < 6) { setResetError('Password must be at least 6 characters.'); return; }
    setResetSaving(true);
    try {
      await api.post(`/users/${resetUserId}/reset-password`, { temporary_password: tempPassword });
      setResetUserId(null);
      flash(`Password reset for ${resetUserName}. They must change it on next login.`);
      load();
    } catch (err) {
      setResetError(err.response?.data?.error || 'Error resetting password.');
    } finally {
      setResetSaving(false);
    }
  };

  const handleOwnPasswordChange = async () => {
    setOwnError('');
    if (ownNew.length < 6) { setOwnError('New password must be at least 6 characters.'); return; }
    if (ownNew !== ownConfirm) { setOwnError('Passwords do not match.'); return; }
    setOwnSaving(true);
    try {
      await api.post('/auth/change-password', {
        user_id: currentUser.user_id,
        current_password: ownCurrent,
        new_password: ownNew,
      });
      setShowOwnReset(false);
      setOwnCurrent(''); setOwnNew(''); setOwnConfirm('');
      flash('Your password has been changed successfully.');
    } catch (err) {
      setOwnError(err.response?.data?.error || 'Error changing password.');
    } finally {
      setOwnSaving(false);
    }
  };

  return (
    <div>
      <div className="page-header">
        <div>
          <h2 className="page-title">User Management</h2>
          <p className="page-subtitle">Manage user accounts and access levels</p>
        </div>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button className="btn btn-secondary btn-sm" onClick={() => { setShowOwnReset(true); setOwnError(''); }}>
            🔐 Change My Password
          </button>
          <button className="btn btn-primary" onClick={openCreate}>＋ New User</button>
        </div>
      </div>

      {successMsg && (
        <div style={{ padding: '12px 16px', background: '#f0fdf4', border: '1px solid #86efac', borderRadius: '8px', color: '#166534', marginBottom: '20px', fontSize: '14px', fontWeight: '500' }}>
          ✅ {successMsg}
        </div>
      )}

      {/* Role legend */}
      <div className="card" style={{ marginBottom: '20px' }}>
        <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap' }}>
          {[
            { role: 'Admin', desc: 'Full access including user management' },
            { role: 'Project Manager', desc: 'Full access to all project modules' },
            { role: 'Developer', desc: 'View projects/tasks, add notes, view allocation' },
          ].map(r => {
            const s = ROLE_BADGE[r.role];
            return (
              <div key={r.role} style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <span style={{ padding: '4px 12px', borderRadius: '20px', fontSize: '12px', fontWeight: '700', background: s.bg, color: s.color, border: `1px solid ${s.border}` }}>{r.role}</span>
                <span style={{ fontSize: '13px', color: '#64748b' }}>{r.desc}</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Users table */}
      <div className="card">
        <div className="card-header">
          <h3 className="card-title">Users ({users.length})</h3>
        </div>
        {loading ? <div className="loading">Loading…</div> : (
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Role</th>
                  <th>Status</th>
                  <th>Password</th>
                  <th>Created</th>
                  <th style={{ minWidth: '160px' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map(u => {
                  const s = ROLE_BADGE[u.role] || ROLE_BADGE['Developer'];
                  const isMe = u.user_id === currentUser.user_id;
                  return (
                    <tr key={u.user_id} style={{ background: isMe ? '#f8f5ff' : undefined }}>
                      <td style={{ fontWeight: '600' }}>
                        {u.name} {isMe && <span style={{ fontSize: '11px', color: '#8b5cf6', fontWeight: '400' }}>(you)</span>}
                      </td>
                      <td style={{ fontSize: '13px', color: '#475569' }}>{u.email}</td>
                      <td>
                        <span style={{ padding: '4px 12px', borderRadius: '20px', fontSize: '12px', fontWeight: '700', background: s.bg, color: s.color, border: `1px solid ${s.border}` }}>
                          {u.role}
                        </span>
                      </td>
                      <td>
                        <span className={`badge ${u.is_active ? 'badge-success' : 'badge-danger'}`}>
                          {u.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td>
                        {u.must_reset_password
                          ? <span className="badge badge-warning" style={{ fontSize: '11px' }}>⚠ Must Reset</span>
                          : <span className="badge badge-success" style={{ fontSize: '11px' }}>✓ Set</span>}
                      </td>
                      <td style={{ fontSize: '13px', color: '#64748b' }}>{u.created_at}</td>
                      <td style={{ whiteSpace: 'nowrap' }}>
                        <button className="btn btn-sm btn-primary" onClick={() => openEdit(u)} style={{ marginRight: '6px' }}>✏️ Edit</button>
                        <button className="btn btn-sm btn-secondary" onClick={() => openReset(u)} style={{ marginRight: '6px', fontSize: '12px' }}>🔑 Reset PW</button>
                        {!isMe && (
                          <button className="btn btn-sm btn-danger" onClick={() => handleDelete(u.user_id, u.name)}>🗑️</button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create / Edit Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">{editingId ? 'Edit User' : 'New User'}</h3>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label className="form-label">Full Name *</label>
                <input className="form-control" name="name" value={form.name} onChange={handleChange} required placeholder="e.g. Jane Smith" />
              </div>
              <div className="form-group">
                <label className="form-label">Email Address *</label>
                <input type="email" className="form-control" name="email" value={form.email} onChange={handleChange} required placeholder="jane@company.com" />
              </div>
              <div className="grid grid-2">
                <div className="form-group">
                  <label className="form-label">Role</label>
                  <select className="form-control" name="role" value={form.role} onChange={handleChange}>
                    {ROLES.map(r => <option key={r}>{r}</option>)}
                  </select>
                </div>
                {editingId && (
                  <div className="form-group" style={{ display: 'flex', alignItems: 'center', gap: '10px', paddingTop: '32px' }}>
                    <input type="checkbox" name="is_active" id="is_active" checked={form.is_active} onChange={handleChange} style={{ width: '18px', height: '18px', cursor: 'pointer' }} />
                    <label htmlFor="is_active" style={{ cursor: 'pointer', fontSize: '14px', fontWeight: '600', color: '#2c3e50' }}>Active Account</label>
                  </div>
                )}
              </div>
              {!editingId && (
                <div className="form-group">
                  <label className="form-label">Temporary Password *</label>
                  <input type="password" className="form-control" name="temporary_password" value={form.temporary_password} onChange={handleChange} placeholder="Min. 6 characters — user must change on first login" />
                  <small style={{ color: '#64748b', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                    The user will be prompted to set a new password when they first sign in.
                  </small>
                </div>
              )}
              {formError && (
                <div style={{ padding: '10px 14px', background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: '8px', color: '#991b1b', fontSize: '14px', marginBottom: '8px' }}>
                  ❌ {formError}
                </div>
              )}
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? 'Saving…' : editingId ? '💾 Update' : '➕ Create User'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Reset Password Modal */}
      {resetUserId && (
        <div className="modal-overlay" onClick={() => setResetUserId(null)}>
          <div className="modal" style={{ maxWidth: '440px' }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">🔑 Reset Password</h3>
            </div>
            <p style={{ color: '#64748b', fontSize: '14px', marginBottom: '20px' }}>
              Set a temporary password for <strong>{resetUserName}</strong>. They will be required to change it on their next login.
            </p>
            <div className="form-group">
              <label className="form-label">Temporary Password</label>
              <input
                type="password"
                className="form-control"
                value={tempPassword}
                onChange={e => setTempPassword(e.target.value)}
                placeholder="Min. 6 characters"
                autoFocus
              />
            </div>
            {resetError && (
              <div style={{ padding: '10px 14px', background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: '8px', color: '#991b1b', fontSize: '14px', marginBottom: '8px' }}>
                ❌ {resetError}
              </div>
            )}
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setResetUserId(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleReset} disabled={resetSaving}>
                {resetSaving ? 'Resetting…' : '🔑 Reset Password'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Change Own Password Modal */}
      {showOwnReset && (
        <div className="modal-overlay" onClick={() => setShowOwnReset(false)}>
          <div className="modal" style={{ maxWidth: '440px' }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">🔐 Change My Password</h3>
            </div>
            <div className="form-group">
              <label className="form-label">Current Password</label>
              <input type="password" className="form-control" value={ownCurrent} onChange={e => setOwnCurrent(e.target.value)} placeholder="Your current password" autoFocus />
            </div>
            <div className="form-group">
              <label className="form-label">New Password</label>
              <input type="password" className="form-control" value={ownNew} onChange={e => setOwnNew(e.target.value)} placeholder="Min. 6 characters" />
            </div>
            <div className="form-group">
              <label className="form-label">Confirm New Password</label>
              <input type="password" className="form-control" value={ownConfirm} onChange={e => setOwnConfirm(e.target.value)} placeholder="Repeat new password" />
            </div>
            {ownError && (
              <div style={{ padding: '10px 14px', background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: '8px', color: '#991b1b', fontSize: '14px', marginBottom: '8px' }}>
                ❌ {ownError}
              </div>
            )}
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowOwnReset(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleOwnPasswordChange} disabled={ownSaving}>
                {ownSaving ? 'Saving…' : '🔐 Change Password'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Users;
